/**
 * Who is off over the next few weeks, at a glance.
 *
 * The leave calendar answers "what happened in August". This answers the
 * question a manager actually opens the dashboard with: how thin is the team
 * about to get. Those are different enough to want different shapes — a month
 * grid is the wrong tool for a fortnight of forward planning, because the weeks
 * that matter are split across two screens of it.
 *
 * So this is a strip of the days ahead, densest where the pressure is: a day
 * with three people off looks different from a day with none, before anybody
 * reads a number. Names sit underneath, because "who" is the follow-up question
 * to "how many" every single time.
 *
 * Scope comes from the server. A manager sees their reporting line, HR and
 * leadership see the company — the same rule as the calendar, so the two never
 * disagree about who is off.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { get } from '../lib/api.js';
import { Card, Avatar, Loading, Empty, Icon } from './ui.jsx';
import { token } from './charts.jsx';
import { todayISO, addDays, isoDay, fmtDateShort, DOW } from '../lib/format.js';

/** Every date from `from` to `to` inclusive, as YYYY-MM-DD. */
function eachDay(from, days) {
  return Array.from({ length: days }, (_, i) => addDays(from, i));
}

export default function UpcomingLeave({ days = 21, title = 'Who is off next' }) {
  const navigate = useNavigate();
  const from = todayISO();
  const to = addDays(from, days - 1);
  const [picked, setPicked] = useState(null);

  const cal = useQuery({
    queryKey: ['leave', 'calendar', 'upcoming', from, to],
    queryFn: () => get('/leave/calendar', { from, to }),
  });

  const leave = cal.data?.data?.leave ?? [];
  const holidays = cal.data?.data?.holidays ?? [];

  /** date -> the approved leave covering it, and the holiday if any. */
  const byDay = useMemo(() => {
    const map = new Map();
    for (const date of eachDay(from, days)) {
      map.set(date, {
        date,
        off: leave.filter((l) => isoDay(l.fromDate) <= date && isoDay(l.toDate) >= date),
        holiday: holidays.find((h) => isoDay(h.date) === date),
      });
    }
    return map;
  }, [leave, holidays, from, days]);

  const cells = [...byDay.values()];
  const busiest = Math.max(1, ...cells.map((c) => c.off.length));
  const peopleOff = new Set(leave.map((l) => l.employee.id)).size;

  // Selected day, or the first one with anybody off — so the list below is
  // never empty while the strip plainly shows somebody is away.
  const shown = picked
    ? byDay.get(picked)
    : cells.find((c) => c.off.length > 0);

  return (
    <Card
      title={title}
      subtitle={`Approved leave · next ${days} days`}
      actions={(
        <button type="button" className="btn btn-sm" onClick={() => navigate('/leave')}>
          <Icon name="calendar" />Full calendar
        </button>
      )}
    >
      {cal.isLoading ? <Loading /> : leave.length === 0 ? (
        <Empty
          icon="plane"
          title="Nobody is off in the next three weeks"
          sub="Approved leave in your scope appears here as soon as it is granted."
        />
      ) : (
        <>
          <div className="txt-sm muted" style={{ marginBottom: 10 }}>
            <b>{peopleOff}</b> {peopleOff === 1 ? 'person' : 'people'} away across {leave.length}{' '}
            {leave.length === 1 ? 'booking' : 'bookings'}
          </div>

          <div className="upl-strip">
            {cells.map((c) => {
              const dow = new Date(`${c.date}T00:00:00Z`).getUTCDay();
              const weekend = dow === 0;
              const count = c.off.length;
              return (
                <button
                  key={c.date}
                  type="button"
                  className={`upl-day${count ? ' has' : ''}${weekend ? ' rest' : ''}${c.holiday ? ' hol' : ''}${shown?.date === c.date ? ' on' : ''}`}
                  // Opacity carries the load so a busy day reads before any
                  // number does; the count is the confirmation, not the signal.
                  style={count ? { '--fill': `${0.18 + 0.82 * (count / busiest)}` } : undefined}
                  onClick={() => setPicked(c.date)}
                  title={`${fmtDateShort(c.date)}${c.holiday ? ` · ${c.holiday.name}` : ''} · ${count} on leave`}
                >
                  <span className="upl-dow">{DOW[dow]}</span>
                  <span className="upl-dn">{Number(c.date.slice(8, 10))}</span>
                  <span className="upl-n">{count || ''}</span>
                </button>
              );
            })}
          </div>

          {shown && (
            <div className="upl-list">
              <div className="txt-sm muted" style={{ margin: '12px 0 6px' }}>
                {fmtDateShort(shown.date)}
                {shown.holiday ? ` · ${shown.holiday.name}` : ''}
                {shown.off.length === 0 ? ' · nobody off' : ` · ${shown.off.length} off`}
              </div>
              {shown.off.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className="upl-person"
                  onClick={() => navigate(`/employee/${l.employee.id}`)}
                >
                  <Avatar name={l.employee.fullName} size="xs" employeeId={l.employee.id} hasPhoto={Boolean(l.employee.photoUrl)} />
                  <span className="upl-name">{l.employee.fullName}</span>
                  <span className="txt-sm muted">
                    {fmtDateShort(l.fromDate)}–{fmtDateShort(l.toDate)}
                  </span>
                  <span className="badge ml-auto" style={{ color: token(l.leaveType.colorToken) }}>
                    {l.leaveType.code}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
